import { useEffect, useMemo } from "react";
import { useGetLink } from "../../services";
import { useTheme } from "../../contexts";
import { buildViewModel } from "./viewmodel/buildViewModel";
import { resolveTokens } from "./viewmodel/resolveTokens";
import { TemplateRenderer } from "./templates/TemplateRenderer";

interface Props {
  profileLinkId: string;
  isEditable: boolean;
  pdfMode: boolean;
  logoUrl?: string | null;
}

/**
 * PDFProfileContainer — the canonical PDF render path (loaded by the pdf-exporter's
 * Puppeteer instance via `/pdf/:profileLinkId`).
 *
 * As of the CV-template refactor (step 1c, variant A — see /CV_TEMPLATES_PLAN.md),
 * this no longer holds the layout itself. It resolves the link into a
 * ProfileViewModel and delegates rendering to the classic template. It still owns
 * setting the organization theme into context (so template colors resolve).
 */
const PDFProfileContainer = (props: Props) => {
  const { setOrganizationTheme } = useTheme();

  const { data: link } = useGetLink(props.profileLinkId, {
    enabled: !!props.profileLinkId,
  });

  // Set organization theme when data loads (memoized to prevent infinite loops)
  const themeData = useMemo(() => {
    if (!link.accountProfile.organization?.id) return null;
    setOrganizationTheme({
      organizationId: link.accountProfile.organization.id,
      organizationName: link.accountProfile.organization.name,
      colors: {
        primary: link.accountProfile.organization.primaryColor || "#3b82f6",
        secondary: link.accountProfile.organization.secondaryColor || "#000",
        accent: "#3b82f6",
      },
      logoId: link.accountProfile.organization.logoId,
      iconLogoId: link.accountProfile.organization.iconLogoId,
    });
    return {
      organizationId: link.accountProfile.organization.id,
      organizationName: link.accountProfile.organization.name,
      colors: {
        primary: link.accountProfile.organization.primaryColor || "#3b82f6",
        secondary: link.accountProfile.organization.secondaryColor || "#000",
        accent: "#3b82f6",
      },
      logoId: link.accountProfile.organization.logoId,
      iconLogoId: link.accountProfile.organization.iconLogoId,
    };
  }, [
    link.accountProfile.organization?.id,
    link.accountProfile.organization?.name,
    link.accountProfile.organization?.logoId,
    link.accountProfile.organization?.iconLogoId,
  ]);

  useEffect(() => {
    if (themeData) {
      setOrganizationTheme(themeData);
    }
  }, [themeData, setOrganizationTheme]);

  if (!link) return null;

  const vm = buildViewModel(link);
  const tokens = resolveTokens(vm);

  // Template selection: `?template=` query override (for testing) → the
  // templateId saved on the link config (set in the link-creator side menu) →
  // classic. `templateId` isn't in the generated ProfileLinkConfig type yet, so
  // it's read through a local cast (test wiring; see TemplateLinkSettings).
  const configTemplateId = (
    vm.linkConfig as { templateId?: string } | undefined
  )?.templateId;
  const queryTemplateId = new URLSearchParams(window.location.search).get(
    "template",
  );
  const templateId = queryTemplateId || configTemplateId || "classic";

  return (
    <TemplateRenderer
      templateId={templateId}
      vm={vm}
      tokens={tokens}
      pdfMode={props.pdfMode}
      isEditable={props.isEditable}
      logoUrl={props.logoUrl}
    />
  );
};

export default PDFProfileContainer;
